import { type ReactNode, useEffect, useId, useState } from 'react';
import type { ListState } from '../lib/list-state';

export function ListFilters({ state, children, searchLabel = 'Search', dates = true }: {
  state: ListState; children?: ReactNode; searchLabel?: string | false; dates?: boolean;
}) {
  const current = state.value('search');
  const [search, setSearch] = useState(current);
  const [expanded, setExpanded] = useState(false);
  const formId = useId();
  const applied = [...new URLSearchParams(state.search)].some(([key, value]) => !['cursor', 'item', 'view'].includes(key) && value);
  useEffect(() => setSearch(current), [current]);
  return <section className="panel list-filter-panel">
    <button type="button" className="list-filters-toggle" aria-controls={formId} aria-expanded={expanded} onClick={() => setExpanded(!expanded)}>{expanded ? 'Hide filters' : searchLabel ? 'Filters and search' : 'Filters'}{applied ? ' · Applied' : ''}</button>
    <form id={formId} className={'list-filters' + (expanded ? ' list-filters--expanded' : '')} aria-label="Filter records" onSubmit={event => { event.preventDefault(); state.filter({ search: search.trim() }); }}>
    {children}
    {searchLabel ? <label className="list-filters__search"><span>{searchLabel}</span><input type="search" maxLength={160} value={search} onChange={event => setSearch(event.target.value)} /></label> : null}
    {dates ? <><label><span>From (UTC)</span><input type="date" value={state.value('since')} max={state.value('until') || undefined} onChange={event => state.filter({ since: event.target.value })} /></label><label><span>Through (UTC)</span><input type="date" value={state.value('until')} min={state.value('since') || undefined} onChange={event => state.filter({ until: event.target.value })} /></label></> : null}
    {searchLabel ? <button type="submit" className="button">Search</button> : null}
    <button type="button" className="button button--quiet" onClick={() => { setSearch(''); state.clear(); }}>Reset filters</button>
  </form></section>;
}

export function FilterSelect({ label, value, options, onChange }: {
  label: string; value: string; options: Array<[string, string]>; onChange(value: string): void;
}) {
  const labelId = useId();
  return <label><span id={labelId}>{label}</span><select aria-labelledby={labelId} value={value} onChange={event => onChange(event.target.value)}>{options.map(([key, title]) => <option key={key} value={key}>{title}</option>)}</select></label>;
}

export function ListPagination({ state, nextCursor, count, busy = false }: {
  state: ListState; nextCursor?: string | null; count: number; busy?: boolean;
}) {
  return <nav className="list-pagination list-pagination--labelled" aria-label="Record pages">
    <button type="button" className="button button--quiet" disabled={state.page === 1 || busy} onClick={state.previous}>Previous</button>
    <span role="status" aria-live="polite">Page {state.page} · {count} {count === 1 ? 'record' : 'records'}</span>
    <button type="button" className="button button--quiet" disabled={!nextCursor || busy} onClick={() => nextCursor && state.next(nextCursor)}>Next</button>
  </nav>;
}

export function ListViews({ state, field = 'status', options }: {
  state: ListState; field?: string; options: Array<[string, string]>;
}) {
  return <nav className="list-views" aria-label="Record views">{options.map(([value, label]) => <button type="button" key={value} aria-pressed={state.value(field) === value} onClick={() => state.filter({ [field]: value })}>{label}</button>)}</nav>;
}
